import React, { useState, useEffect, useContext } from "react";
import SEO from "../components/SEO";
import { showToast } from "../components/ToastNotification";
import useAuthToken from "../hooks/useAuthToken";
import { Link, useNavigate } from "react-router-dom";
import { FaUser, FaSync, FaCog } from "react-icons/fa";
import { usePricing } from "../contexts/PricingContext";
import { isCurrentUserAdmin } from "../utils/adminUtils";
import { AuthContext } from "../contexts/AuthContext";
import analytics from "../services/analytics";

const Products = () => {
  const { fetchWithAuth } = useAuthToken();
  const navigate = useNavigate();
  const { pricing, isLoading: loadingPricing, refreshPricing, lastFetchTime, isCached, error } = usePricing();
  const { isAuthenticated } = useContext(AuthContext);
  
  const [loadingPriceId, setLoadingPriceId] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("year");
  const [userPlan, setUserPlan] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load plan status when authenticated, clear when not
  useEffect(() => {
    if (isAuthenticated) {
      const status =
        localStorage.getItem("plan_status") ||
        sessionStorage.getItem("plan_status");
      if (status) {
        try {
          const parsed = JSON.parse(status);
          setUserPlan(parsed);
          
          // Set billing period to match user's current plan
          if (parsed.plan) {
            const planParts = parsed.plan.split('_');
            if (planParts.length > 1) {
              const currentPeriod = planParts[1]; // 'month' or 'year'
              setBillingPeriod(currentPeriod);
            }
          }
        } catch (err) {
          console.error("Failed to parse plan status", err);
          setUserPlan(null);
        }
      }
      
      // Check admin status
      setIsAdmin(isCurrentUserAdmin());
    } else {
      // Clear plan data when not authenticated
      setUserPlan(null);
      setIsAdmin(false);
    }
  }, [isAuthenticated]);

  // Track when users view pricing plans
  useEffect(() => {
    if (pricing && Object.keys(pricing).length > 0) {
      Object.entries(pricing).forEach(([planKey, tier]) => {
        const price = tier.prices?.[billingPeriod] || 0;
        analytics.trackSubscriptionPlanView(tier.title, price);
      });
    }
  }, [pricing, billingPeriod]);

  const handleSubscribe = async (priceId, route, planName, billingPeriod, price) => {
    if (!isAuthenticated) {
      analytics.trackButtonClick('subscribe_not_authenticated', 'products');
      showToast("Please sign in to subscribe to a plan", "info");
      navigate("/auth/signin");
      return;
    }

    // Track subscription checkout start
    analytics.trackSubscriptionCheckoutStart(planName, billingPeriod);
    analytics.trackSubscriptionPlanSelect(planName, billingPeriod, price);

    setLoadingPriceId(priceId);
    showToast("Redirecting to checkout...");

    try {
      const res = await fetchWithAuth(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/create-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            price_id: priceId,
            success_url: `${process.env.REACT_APP_URL}${route}?payment_success=true&plan=${encodeURIComponent(planName)}&period=${billingPeriod}&price=${price}`,
            cancel_url: `${process.env.REACT_APP_URL}/products`,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok || !data?.url) {
        showToast(data?.detail || "Failed to start Stripe checkout", "error");
        return;
      }

      window.location.href = data.url;
    } catch (error) {
      if (error.message.includes("Authentication required")) {
        showToast("Please sign in to subscribe", "error");
        navigate("/auth/signin");
      } else {
        showToast("Checkout failed. Please try again.", "error");
        console.error("Stripe checkout error:", error);
      }
    } finally {
      setLoadingPriceId(null);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "RiskDiscover Risk Assessment Platform", 
    "description": "AI-powered risk assessment platform with flexible pricing plans for businesses of all sizes",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web-based",
    "brand": {
      "@type": "Brand",
      "name": "RiskDiscover"
    },
    "publisher": {
      "@type": "Organization",
      "name": "moredevs.ai",
      "url": "https://moredevs.ai"
    },
    "category": "Business Intelligence Software",
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": "Insurance Professionals"
    },
    "areaServed": "Europe",
    "featureList": [
      "AI-powered company risk analysis",
      "Dynamic risk scoring algorithms",
      "Professional PDF report generation", 
      "European company database coverage",
      "Real-time risk assessment",
      "Industry-specific risk benchmarking"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Risk Assessment Pricing Plans",
      "description": "Flexible subscription plans for businesses of all sizes"
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16 flex-1 font-['Archivo']">
      <SEO 
        title="Risk Assessment Plans & Pricing | RiskDiscover AI Platform"
        description="Choose the perfect risk assessment plan for your business. Flexible pricing for startups to enterprises. Start with our free trial today."
        canonical="/products"
        structuredData={structuredData}
      />
      {/* Page Header */}
      <div className="text-center mb-14">
        <h1 className="text-5xl font-extrabold text-[rgb(22,0,79)] mb-3 drop-shadow-sm">
          RiskDiscover Assessment Tool
        </h1>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Choose your level, unlock insights, and assess risk like never before.
        </p>
      </div>

      {/* Authentication Status */}
      {!isAuthenticated && (
        <div className="max-w-2xl mx-auto bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-3xl p-8 shadow-lg mb-12">
          <div className="text-center">
            <FaUser className="mx-auto text-blue-600 text-4xl mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Create an Account to Get Started</h2>
            <p className="text-gray-600 mb-6">
              Sign up or log in to subscribe to a plan and start assessing risk like never before.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/auth/signup" 
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Create Account
              </Link>
              <Link 
                to="/auth/signin" 
                className="bg-white text-blue-600 border border-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
      
      {/* Billing Period and Pricing Controls */}
      <div className="flex flex-col items-center gap-4 mb-14">
        {/* Free Credits Banner */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg px-6 py-3 mb-2">
          <p className="text-center text-sm font-medium text-gray-700">
            ✨ Start with <span className="font-bold text-green-700">free credits</span> • No credit card required
          </p>
        </div>

        <div className="flex justify-center gap-5">
          <button
            onClick={() => {
              setBillingPeriod("month");
              analytics.trackButtonClick('billing_period_month', 'products');
            }}
            className={`px-6 py-3 rounded-full text-base font-semibold border shadow-md transition duration-300 ${
              billingPeriod === "month"
                ? "bg-[rgb(22,0,79)] text-white border-[rgb(22,0,79)] shadow-lg"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}
            aria-pressed={billingPeriod === "month"}
          >
            Monthly billing
          </button>
          <button
            onClick={() => {
              setBillingPeriod("year");
              analytics.trackButtonClick('billing_period_year', 'products');
            }}
            className={`px-6 py-3 rounded-full text-base font-semibold border shadow-md transition duration-300 ${
              billingPeriod === "year"
                ? "bg-[rgb(22,0,79)] text-white border-[rgb(22,0,79)] shadow-lg"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}
            aria-pressed={billingPeriod === "year"}
          >
            Yearly billing
          </button>
        </div>
        
        {/* Admin-Only Pricing Controls */}
        {isAdmin && isCached && lastFetchTime && (
          <div className="flex items-center gap-4 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
            <FaCog className="text-blue-600" />
            <span className="text-blue-800 font-medium">
              Admin: Prices cached {lastFetchTime.toLocaleDateString()} at {lastFetchTime.toLocaleTimeString()}
            </span>
            <button
              onClick={() => {
                refreshPricing();
                showToast("Price cache refreshed for all users", "success");
              }}
              disabled={loadingPricing}
              className="flex items-center gap-2 px-3 py-1 rounded-md border border-blue-300 bg-blue-100 hover:bg-blue-200 transition-colors disabled:opacity-50 text-blue-800 font-medium"
              title="Refresh pricing cache for all users"
            >
              <FaSync className={`w-3 h-3 ${loadingPricing ? 'animate-spin' : ''}`} />
              {loadingPricing ? 'Refreshing...' : 'Refresh Price Cache'}
            </button>
          </div>
        )}
      </div>

      {loadingPricing ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading pricing information...</p>
          </div>
        </div>
      ) : error && Object.keys(pricing).length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-center max-w-md">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Pricing Unavailable</h3>
            <p className="text-gray-600 mb-6">
              Unable to load pricing information. Please check your internet connection and try again.
            </p>
            <button
              onClick={() => {
                refreshPricing();
                showToast("Retrying to load pricing...", "info");
              }}
              disabled={loadingPricing}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <FaSync className={`mr-2 h-4 w-4 ${loadingPricing ? 'animate-spin' : ''}`} />
              {loadingPricing ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        </div>
      ) : Object.keys(pricing).length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Plans Available</h3>
            <p className="text-gray-600">Pricing plans will be displayed here once loaded.</p>
          </div>
        </div>
      ) : (
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(pricing).map(([planKey, tier], idx) => {
            const price = tier.prices?.[billingPeriod] || 0;
            const priceId = tier.price_ids?.[billingPeriod];

            const planDisplayKey = `${planKey}_${billingPeriod}`;
            const isCurrent = userPlan?.plan === planDisplayKey;
            const hasActivePlan = userPlan?.plan && userPlan?.plan !== "";
            
            // Determine upgrade/downgrade logic
            const userPlanTier = userPlan?.plan?.split('_')[0]; // basic, premium, enterprise
            const currentTier = planKey; // basic, premium, enterprise
            
            const tierOrder = { basic: 1, premium: 2, enterprise: 3 };
            const currentTierLevel = tierOrder[currentTier] || 0;
            const userTierLevel = tierOrder[userPlanTier] || 0;
            
            const isUpgrade = hasActivePlan && currentTierLevel > userTierLevel;
            const isDowngrade = hasActivePlan && currentTierLevel < userTierLevel;

            return (
              <div
                key={idx}
                className={`bg-gradient-to-br ${tier.color || 'from-gray-100 to-gray-200'} p-7 rounded-3xl shadow-xl hover:shadow-2xl border ${tier.highlight ? 'border-[rgb(22,0,79)] border-2' : 'border-gray-200'} hover:border-[rgb(22,0,79)] transition duration-300 flex flex-col justify-between relative`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[rgb(22,0,79)] text-white px-4 py-1 text-sm font-semibold rounded-full">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-extrabold text-[rgb(71,63,125)] mb-2 tracking-tight">
                    {tier.title}
                  </h2>
                  {tier.audience && (
                    <p className="text-sm text-gray-500 italic mb-4 leading-relaxed">
                      {tier.audience}
                    </p>
                  )}
                  {tier.description && (
                    <p className="text-gray-700 text-sm mb-6">{tier.description}</p>
                  )}

                  {!tier.coming_soon && tier.prices && (
                    <div className="mb-6 text-xl text-[rgb(22,0,79)] font-semibold tracking-wide">
                      €{billingPeriod === "month" ? price : (price / 12).toFixed(2)} / month
                      {billingPeriod === "year" && (
                        <span className="text-sm text-gray-600 ml-2">(billed annually)</span>
                      )}
                    </div>
                  )}

                  {(tier.features || tier.base_features) && (
                    <ul className="mb-8 list-disc list-inside text-sm text-gray-800 space-y-1">
                      {/* Handle new structure with dynamic credits or fallback to old structure */}
                      {tier.base_features ? (
                        <>
                          {/* New structure: base features + dynamic credits + additional features */}
                          {tier.base_features.map((feature, i) => (
                            <li key={i}>{feature}</li>
                          ))}
                          <li key="credits">
                            {tier.credits?.[billingPeriod] || 0} credits included
                          </li>
                          {tier.additional_features?.map((feature, i) => (
                            <li key={`additional-${i}`}>{feature}</li>
                          ))}
                        </>
                      ) : (
                        /* Fallback to old structure */
                        tier.features.map((feature, i) => (
                          <li key={i}>{feature}</li>
                        ))
                      )}
                    </ul>
                  )}
                </div>

                <div>
                  {isCurrent ? (
                    <div className="space-y-2">
                      <div className="w-full bg-green-100 text-green-800 py-3 text-center rounded-xl font-semibold border border-green-300">
                        ✓ Current Plan
                      </div>
                      <Link 
                        to={tier.route || "/dashboard"}
                        className="block w-full bg-blue-600 text-white py-2 text-center rounded-xl font-medium hover:bg-blue-700 transition text-sm"
                      >
                        Access Tools
                      </Link>
                    </div>
                  ) : tier.coming_soon ? (
                    <button
                      disabled
                      className="w-full bg-gray-300 text-gray-600 py-3 rounded-xl font-semibold cursor-not-allowed"
                    >
                      Launching soon...
                    </button>
                  ) : !isAuthenticated ? (
                    <button
                      onClick={() => {
                        analytics.trackButtonClick('signup_from_subscription', 'products');
                        navigate("/auth/signup");
                      }}
                      className="w-full bg-[rgb(71,63,125)] text-white py-3 rounded-xl font-semibold hover:brightness-110 transition"
                    >
                      Sign Up to Subscribe
                    </button>
                  ) : hasActivePlan && isDowngrade ? (
                    <div className="space-y-2">
                      <button
                        disabled
                        className="w-full bg-gray-200 text-gray-500 py-3 rounded-xl font-semibold cursor-not-allowed"
                      >
                        Downgrade Not Available
                      </button>
                      <Link 
                        to="/profile"
                        className="block w-full bg-gray-600 text-white py-2 text-center rounded-xl font-medium hover:bg-gray-700 transition text-sm"
                      >
                        Manage Current Plan
                      </Link>
                    </div>
                  ) : hasActivePlan && isUpgrade ? (
                    <div className="space-y-2">
                      <button
                        onClick={() => handleSubscribe(priceId, tier.route || "#", tier.title, billingPeriod, price)}
                        disabled={loadingPriceId === priceId || !priceId}
                        className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition disabled:opacity-50"
                      >
                        {loadingPriceId === priceId ? "Processing..." : "✨ Upgrade Plan"}
                      </button>
                      <p className="text-xs text-gray-600 text-center">
                        Immediate access after upgrade
                      </p>
                    </div>
                  ) : hasActivePlan ? (
                    <div className="space-y-2">
                      <button
                        onClick={() => handleSubscribe(priceId, tier.route || "#", tier.title, billingPeriod, price)}
                        disabled={loadingPriceId === priceId || !priceId}
                        className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-50"
                      >
                        {loadingPriceId === priceId ? "Processing..." : "Switch Plan"}
                      </button>
                      <Link 
                        to="/profile"
                        className="block w-full bg-gray-500 text-white py-2 text-center rounded-xl font-medium hover:bg-gray-600 transition text-sm"
                      >
                        Manage Current Plan
                      </Link>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(priceId, tier.route || "#")}
                      disabled={loadingPriceId === priceId || !priceId}
                      className="w-full bg-[rgb(71,63,125)] text-white py-3 rounded-xl font-semibold hover:brightness-110 transition disabled:opacity-50"
                    >
                      {loadingPriceId === priceId ? "Redirecting..." : "Subscribe"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Products;
